import React from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { DollarSign, Briefcase, TrendingUp } from "lucide-react";

import { formatCurrency } from "@/lib/utils";

interface BidDashboardProps {
  bid: any;
  bidServices: any[];
  bidSummary: any;
  costSummary: any;
  profitAnalysis: any;
}

export const BidDashboard: React.FC<BidDashboardProps> = ({
  bid,
  bidServices,
  bidSummary,
  costSummary,
  profitAnalysis,
}) => {
  return (
    <Card className="p-6 md:col-span-2">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Bid Dashboard</h2>
      </div>
      <Separator className="mb-4" />

      {/* Bid Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-xl border bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Monthly Revenue</h3>
            <DollarSign className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold">
            {bidSummary?.recommendedMonthlyPrice !== undefined
              ? formatCurrency(bidSummary.recommendedMonthlyPrice)
              : bid.value || "$0.00"}
          </p>
          <p className="mt-1 text-xs text-gray-500">Recommended contract value</p>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Profit Margin</h3>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold">
            {profitAnalysis?.grossProfitMargin !== undefined
              ? `${profitAnalysis.grossProfitMargin.toFixed(1)}%`
              : "25%"}
          </p>
          <p className="mt-1 text-xs text-gray-500">Target gross profit</p>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Services</h3>
            <Briefcase className="h-5 w-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold">{bidServices ? bidServices.length : 0}</p>
          <div className="mt-1 flex items-center gap-2">
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
              Cleaning: {bidServices ? bidServices.filter((s) => s.type === "cleaning").length : 0}
            </span>
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
              Floorcare:{" "}
              {bidServices ? bidServices.filter((s) => s.type === "floorcare").length : 0}
            </span>
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="mb-6">
        <h3 className="text-md mb-3 font-semibold">Financial Summary</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-blue-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-600">Revenue</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-500">Monthly</p>
                <p className="text-lg font-semibold">
                  {bidSummary?.recommendedMonthlyPrice !== undefined
                    ? formatCurrency(bidSummary.recommendedMonthlyPrice)
                    : bid.value || "$0.00"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Annual</p>
                <p className="text-lg font-semibold">
                  {bidSummary?.recommendedAnnualPrice !== undefined
                    ? formatCurrency(bidSummary.recommendedAnnualPrice)
                    : bid.value
                      ? formatCurrency(parseFloat(bid.value.replace(/[^0-9.-]+/g, "")) * 12)
                      : "$0.00"}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-green-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-600">Costs</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-500">Monthly</p>
                <p className="text-lg font-semibold">
                  {profitAnalysis?.totalCost !== undefined
                    ? formatCurrency(profitAnalysis.totalCost)
                    : costSummary?.totalMonthlyCost !== undefined
                      ? formatCurrency(costSummary.totalMonthlyCost)
                      : "$0.00"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Startup</p>
                <p className="text-lg font-semibold">
                  {costSummary?.totalStartupCost !== undefined
                    ? formatCurrency(costSummary.totalStartupCost)
                    : "$0.00"}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-purple-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-gray-600">Profit</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-500">Gross</p>
                <p className="text-lg font-semibold">
                  {profitAnalysis?.grossProfit !== undefined
                    ? formatCurrency(profitAnalysis.grossProfit)
                    : "$0.00"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Net</p>
                <p className="text-lg font-semibold">
                  {profitAnalysis?.netProfit !== undefined
                    ? formatCurrency(profitAnalysis.netProfit)
                    : "$0.00"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Breakdown */}
      <div className="mb-6">
        <h3 className="text-md mb-3 font-semibold">Service Breakdown</h3>
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-blue-500"></div>
              <span className="text-sm">Cleaning Services</span>
            </div>
            <span className="text-sm font-medium">
              {bidSummary?.totalCleaningPrice || bidSummary?.totalCleaningCost
                ? formatCurrency(bidSummary.totalCleaningPrice || bidSummary.totalCleaningCost)
                : "$0.00"}
            </span>
          </div>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-green-500"></div>
              <span className="text-sm">Floorcare Services</span>
            </div>
            <span className="text-sm font-medium">
              {bidSummary?.totalFloorcarePrice || bidSummary?.totalFloorcareCost
                ? formatCurrency(bidSummary.totalFloorcarePrice || bidSummary.totalFloorcareCost)
                : "$0.00"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-gray-300"></div>
              <span className="text-sm">Total Square Footage</span>
            </div>
            <span className="text-sm font-medium">
              {bidServices
                ? `${bidServices.reduce((total, service) => total + (service.squareFootage || 0), 0).toLocaleString()} sq ft`
                : "0 sq ft"}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BidDashboard;
